if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        if (!this.validateExtraFields()) {
          return;
        }

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }

      validateExtraFields() {
        const occasionSelect = this.form.querySelector('[data-occasion-select]');
        const guestsSelect = this.form.querySelector('[data-guests-select]');
        const guestsContainer = this.form.querySelector('[data-guests-container]');
        const errorElement = this.form.querySelector('[data-extra-error]');

        if (!occasionSelect && !guestsSelect) {
          return true;
        }

        const setError = (message) => {
          if (!errorElement) return;
          errorElement.textContent = message || '';
          errorElement.toggleAttribute('hidden', !message);
        };

        setError('');

        if (occasionSelect && !occasionSelect.value) {
          setError('Please select an occasion.');
          occasionSelect.focus();
          return false;
        }

        let guestsCount = 1;
        if (guestsSelect && guestsSelect.value) {
          guestsCount = parseInt(guestsSelect.value, 10);
        }

        if (guestsCount > 1 && guestsContainer) {
          const guestInputs = guestsContainer.querySelectorAll('input[data-guest-name-input]');
          if (!guestInputs.length) {
            // Ensure inputs exist if the value is greater than 1
            this.buildGuestInputs(guestsCount);
          }

          const updatedGuestInputs = guestsContainer.querySelectorAll('input[data-guest-name-input]');
          for (const input of updatedGuestInputs) {
            if (!input.value.trim()) {
              setError('Please enter a name for each guest.');
              input.focus();
              return false;
            }
          }
        }

        return true;
      }

      buildGuestInputs(guestsCount) {
        const guestsContainer = this.form.querySelector('[data-guests-container]');
        if (!guestsContainer) return;

        guestsContainer.innerHTML = '';

        if (!guestsCount || guestsCount <= 1) {
          return;
        }

        for (let i = 1; i <= guestsCount; i += 1) {
          const card = document.createElement('div');
          card.classList.add('guest-card');

          const header = document.createElement('div');
          header.classList.add('guest-card__header');

          const title = document.createElement('span');
          title.classList.add('guest-card__title');
          title.textContent = `Person ${i}`;

          header.appendChild(title);

          const body = document.createElement('div');
          body.classList.add('guest-card__body');

          const label = document.createElement('label');
          label.classList.add('guest-card__label');
          label.setAttribute('for', `Guest-${i}-name`);
          label.textContent = 'Name';

          const input = document.createElement('input');
          input.type = 'text';
          input.id = `Guest-${i}-name`;
          input.name = `properties[Guest ${i} Name]`;
          input.classList.add('field__input', 'guest-card__input');
          input.setAttribute('data-guest-name-input', 'true');

          body.appendChild(label);
          body.appendChild(input);

          card.appendChild(header);
          card.appendChild(body);
          guestsContainer.appendChild(card);
        }
      }

      connectedCallback() {
        if (super.connectedCallback) {
          super.connectedCallback();
        }

        const guestsSelect = this.form.querySelector('[data-guests-select]');
        if (guestsSelect) {
          guestsSelect.addEventListener('change', () => {
            const value = parseInt(guestsSelect.value || '1', 10);
            this.buildGuestInputs(value);
          });

          const initialValue = parseInt(guestsSelect.value || '1', 10);
          this.buildGuestInputs(initialValue);
        }
      }
    }
  );
}